// Mich-CA Portal — Shared UI Components

// ── Page Header ──────────────────────────────────────────────────────────────
export function PageHeader({ icon, title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <div className="page-title">
          <span style={{ fontSize: 22, color: 'var(--accent)' }}>{icon}</span>
          <h1>{title}</h1>
        </div>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}

// ── Section Title ────────────────────────────────────────────────────────────
export function SectionTitle({ children, color = 'var(--accent)' }) {
  return (
    <div className="section-title">
      <div className="section-title-bar" style={{ background: color }} />
      <span className="section-title-text" style={{ color }}>{children}</span>
    </div>
  );
}

// ── Field ────────────────────────────────────────────────────────────────────
export function Field({ label, note, required, error, children }) {
  return (
    <div className="field">
      <label className="field-label">
        {label}{required && <span className="field-required">*</span>}
      </label>
      {children}
      {note && <p className="note">{note}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

// ── Data Table ───────────────────────────────────────────────────────────────
export function DataTable({ headers, rows, accentColor = 'var(--accent)', emptyMessage = 'No data' }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{ background: `color-mix(in srgb, ${accentColor} 10%, transparent)` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} style={{ textAlign: 'center', color: 'var(--dim)', padding: '24px' }}>{emptyMessage}</td></tr>
          ) : rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Loading Spinner ───────────────────────────────────────────────────────────
export function Spinner({ size = 20 }) {
  return <div className="spinner" style={{ width: size, height: size }} />;
}

export function LoadingPage() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
      <Spinner size={28} />
      <span style={{ color: 'var(--mid)', fontSize: 14 }}>Loading...</span>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
export function EmptyState({ icon = '○', title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: 36, marginBottom: 12, color: 'var(--dim)' }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--mid)', marginBottom: 6 }}>{title}</div>
      {description && <p style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 16 }}>{description}</p>}
      {action}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 16, fontWeight: 800 }}>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    active: 'tag-green', confirmed: 'tag-green',
    draft: 'tag-gold', pending: 'tag-gold', conflict: 'tag-gold',
    completed: 'tag-blue',
    suspended: 'tag-red', cancelled: 'tag-red', error: 'tag-red',
    unscheduled: 'tag-purple',
  };
  return <span className={`tag ${map[status] || 'tag-blue'}`}>{status}</span>;
}

// ── Confirmation Dialog ───────────────────────────────────────────────────────
export function ConfirmDialog({ message, onConfirm, onCancel, danger = false }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-body" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>{danger ? '⚠️' : '?'}</div>
          <p style={{ fontSize: 14, marginBottom: 24, color: 'var(--text)' }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
              {danger ? 'Delete' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Day Picker ────────────────────────────────────────────────────────────────
export function DayPicker({ value = [], onChange }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const toggle = (d) => onChange(value.includes(d) ? value.filter(x => x !== d) : [...value, d]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {days.map(d => (
        <div key={d} className={`day-chip ${value.includes(d) ? 'active' : ''}`} onClick={() => toggle(d)}>{d}</div>
      ))}
    </div>
  );
}

// ── Pool Picker ───────────────────────────────────────────────────────────────
export function PoolPicker({ value = [], onChange, max = 7 }) {
  const pools = 'ABCDEFGHIJKLMNO'.split('').slice(0, max);
  const toggle = (p) => onChange(value.includes(p) ? value.filter(x => x !== p) : [...value, p]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {pools.map(p => (
        <div key={p}
          onClick={() => toggle(p)}
          style={{
            width: 34, height: 34, borderRadius: 6, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${value.includes(p) ? 'var(--accent)' : 'var(--border)'}`,
            background: value.includes(p) ? 'rgba(0,200,150,.12)' : 'transparent',
            color: value.includes(p) ? 'var(--accent)' : 'var(--dim)',
          }}>{p}</div>
      ))}
    </div>
  );
}
